import * as _ from 'lodash-es';
import { BaseModel, BaseWithId, WhoAndWhen } from '@app/shared/models/base';
import { BaseList } from '@app/shared/models/list';
import { FileType, FileTypes } from '@app/shared/models/file-type';
import { OP, OpData } from '@app/core/services/base/base-service-with-bundle.service';
import { Thumbnailable } from '@app/shared/components/file-upload/thumb-nail-base.component';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { SmpUtils } from '@app/core/helpers/utilities';
import { Role } from '@app/core/authentication/permission';
import { ActionListItem } from '@app/shared/components/action-list/action-list.component';

export class DrFileMetaProperties extends BaseModel {
    public signable: string; // backend needs string 'true' here, not a boolean

    constructor(json?: any) {
        super(json);
    }
}

export enum FileUploadStatus {
    LOADING,
    READY,
    ERROR
}

export interface DrFileMetaOpData extends OpData {
    // below properties only used by the frontend, backend does not use this
    id: string;
    document_type?: string;
}

export class DrFileMeta extends BaseWithId
                        implements Thumbnailable, WhoAndWhen {

    public category: string;
    public client_id: string;
    public country_id: string;
    public filename: string;
    public lastmodified: Date;
    public link: string;
    public order: number;
    public version: number;
    public properties: DrFileMetaProperties;
    public fileType: FileType;
    public status: FileUploadStatus;
    public description: string;
    public not_selectable: boolean;
    public lastmodifiedby: string;
    public document_type: string;
    public creator_role: Role;
    public is_template: boolean;
    public document_key: string;
    public is_placeholder: boolean;

    get whoAndWhen(): string {
        return `${this.lastmodified && this.lastmodified.drDateTimeString() || ''}${this.lastmodifiedby ? (' - ' + this.lastmodifiedby) : ''}`;
    }

    public readonly trans = {
        version: marker('file_meta.version'),
        lastmodified: marker('file_meta.last_modified')
    };

    get reorderPath() {
        return `${this.id}/metadata/order`;
    }

    get thumbnailLink(): string {
        return this.link;
    }

    get thumbnailFileName(): string {
        return this.filename;
    }

    get thumbnailStatus(): FileUploadStatus {
        return this.status;
    }

    constructor(json?: any) {
        super(json);
    }

    public toQueryParams(): any {
        const params: any = {
            filename: this.filename,
            order: this.order
        };

        if (this.document_type) {
            params.document_type = this.document_type;
        }

        if (this.creator_role) {
            params.creator_role = this.creator_role;
        }

        return params;
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            lastmodified: json && json.lastmodified ? new Date(json.lastmodified) : null,
            properties: new DrFileMetaProperties(json && json.properties),
            fileType: json ? (json.filename && SmpUtils.getExtensionFromFileName(json.filename) ? FileTypes.getByKey(SmpUtils.getExtensionFromFileName(json.filename)) : (json.is_template ? FileTypes.XSLT : null)) : null,
            status: json && json.id ? FileUploadStatus.READY : null
        });
    }

}

export class DrFileMetaList extends BaseModel implements ActionListItem {
    public document_type: string;
    public actionListDisplayName: string;
    public entries: DrFileMeta[];

    get nextOrder(): number {
        const highestOrderMeta = _.maxBy(this.entries, (fileMeta: DrFileMeta) => fileMeta.order);
        return highestOrderMeta && highestOrderMeta.order ? (highestOrderMeta.order) + 1 : 1;
    }

    get hasItems(): boolean {
        return this.entries && this.entries.length > 0;
    }

    constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            entries: json && json.entries ? json.entries.map(item => new DrFileMeta(item)) : new Array<DrFileMeta>()
        });
    }

    public addFileMetaData(fileMetaData: DrFileMeta): void {
        this.entries.push(fileMetaData);
    }

    public getFileMetaData(id: string): DrFileMeta {
        return this.entries.find(fileMeta => fileMeta.id === id);
    }

    // We do not  update the order of the DrFileMeta objects yet in this method
    // The order is only updated after a successful patch of the DrFileMetaOpData[] returned by this method
    // The updateFileMetaOrderAfterSuccessFullPatch(patchData: DrFileMetaOpData[]) method on DrFileMetaCategoryList can be used for this purpose
    public removeFileMetaData(fileMetaData: DrFileMeta): DrFileMetaOpData[] {
        // First check if the metaList has gaps in the current orders (copy and correct order)
        let fixedMetaListOrder = this.fixMetaDataOrder();

        // Remove the file from the original list
        this.entries = _.filter(this.entries, (metaData) => metaData.id !== fileMetaData.id );

        // Find the deletedfileMetaData in the fixed list
        const fixedDeletedFileMetaData = fixedMetaListOrder.find(fileMeta => fileMeta.id === fileMetaData.id);
        const removedIdx = fixedMetaListOrder.indexOf(fixedDeletedFileMetaData);

        // Remove the file from the fixed list
        fixedMetaListOrder = _.filter(fixedMetaListOrder, (metaData) => metaData.id !== fileMetaData.id );

        // Create a new array for the reorder patchdata
        const reorderedFileMeta = new Array<DrFileMetaOpData>();

        // Loop over the fixed list starting from the index of the removed file
        // and add each of them to the patch list with an order reduced by 1
        // if this value is different from the original
        for (let i = removedIdx; i < fixedMetaListOrder.length; i++) {
            const metaData = fixedMetaListOrder[i];
            const originalMetaData = this.entries[i];
            let updatedOrder = metaData.order;
            updatedOrder--;

            if (originalMetaData.order !== updatedOrder) {
                reorderedFileMeta.push({
                    id: metaData.id,
                    document_type: metaData.document_type,
                    op: OP.replace,
                    path: metaData.reorderPath,
                    value: updatedOrder
                });
            }
        }

        return reorderedFileMeta;
    }

    // We do not  update the order of the DrFileMeta objects yet in this method
    // The order is only updated after a successful patch of the DrFileMetaOpData[] returned by this method
    // The updateFileMetaOrderAfterSuccessFullPatch(patchData: DrFileMetaOpData[]) method on DrFileMetaCategoryList can be used for this purpose
    public reorderFileMetaData(movedFileMetaData: DrFileMeta, newOrderIndex: number): DrFileMetaOpData[] {
        // First check if the metaList has gaps in the current orders
        const fixedMetaListOrder = this.fixMetaDataOrder();

        // Find the movedfileMetaData in the fixed list
        const fixedMovedFileMetaData = fixedMetaListOrder.find(fileMeta => fileMeta.id === movedFileMetaData.id);

        // Create a new array for the  reorder patchdata
        const reorderedFileMeta = new Array<DrFileMetaOpData>();

        // Loop over the fixed list and add patches if needed
        for (let i = 0; i < fixedMetaListOrder.length; i++) {
            const originalMetaData = this.entries[i];
            const metaData = fixedMetaListOrder[i];

            let updatedOrder = metaData.order;

            if (fixedMovedFileMetaData.order > newOrderIndex && (metaData.order >= newOrderIndex && metaData.order < fixedMovedFileMetaData.order)) {
                // If the order of the meta data in the fixed list is:
                // BIGGER than or EQUAL to the index the moved file will be inserted in
                // and SMALLER than the 'old' order of the moved file
                // than it's order needs to go UP by one
                updatedOrder++;
            } else if (fixedMovedFileMetaData.order < newOrderIndex && (metaData.order <= newOrderIndex && metaData.order > fixedMovedFileMetaData.order)) {
                // If the order of the meta data in the fixed list is:
                // SMALLER than or EQUAL to the index the moved file will be inserted in
                // and BIGGER than the 'old' order of the moved file
                // than it's order needs to go DOWN by one
                updatedOrder--;
            }

            // If the order of the original is different than what we have calculated
            // This can be because it wa a file between the old and new order of the moved file
            // or because of fixing of the gaps in the order of the files
            // We add it to the patch array
            if (originalMetaData.order !== updatedOrder) {
                reorderedFileMeta.push({
                    id: metaData.id,
                    document_type: metaData.document_type,
                    op: OP.replace,
                    path: metaData.reorderPath,
                    value: updatedOrder
                });
            }
        }

        reorderedFileMeta.push({
            id: movedFileMetaData.id,
            document_type: movedFileMetaData.document_type,
            op: OP.replace,
            path: movedFileMetaData.reorderPath,
            value: newOrderIndex
        });

        return reorderedFileMeta;
    }

    public sortFileMetaDatabyOrder(): void {
        if (this.entries) {
            this.entries = _.sortBy(this.entries, fileMeta => fileMeta.order);
        }
    }

    // Method checks if there are gaps in the order of the filesMetadata list
    // Returns a list of DrFileMeta with correct order
    private fixMetaDataOrder(): DrFileMeta[] {
        // first we sort the array by order
        this.sortFileMetaDatabyOrder();

        // We create a copy of the array
        const copiedMetaList = [...this.entries];

        // We set the correct order based on the index in the sorted array
        for (let i = 0; i < copiedMetaList.length; i++) {
            const fileMeta = copiedMetaList[i];
            const correctOrder = i + 1; // Order starts counting from 1, not from 0
            fileMeta.order = correctOrder;
        }

        // We return the sorted and updated order array
        return copiedMetaList;
    }
}

export class DrFileMetaCategoryList extends BaseList<DrFileMetaList> {
    constructor(json?: any) {
        super(DrFileMetaList, json);
    }

    private getOrCreateFileMetaListForCategory(category: string): DrFileMetaList {
        let fileMetaList = this.getFilesByCategory(category);

        if (!fileMetaList) {
            this.items.push(new DrFileMetaList({ document_type: category }));
            fileMetaList = this.getFilesByCategory(category);
        }

        return fileMetaList;
    }

    public getFilesByCategory(category: string): DrFileMetaList {
        return _.find(this.items, (list: DrFileMetaList) => list.document_type === category);
    }

    public addFileMeta(fileMetaData: DrFileMeta): void {
        if (fileMetaData && fileMetaData.document_type) {
            const fileMetaList = this.getOrCreateFileMetaListForCategory(fileMetaData.document_type);
            fileMetaList.addFileMetaData(fileMetaData);
        }
    }

    public removeFileMeta(fileMetaData: DrFileMeta): DrFileMetaOpData[]  {
        if (fileMetaData && fileMetaData.document_type) {
            const fileMetaList = this.getFilesByCategory(fileMetaData.document_type);
            return fileMetaList.removeFileMetaData(fileMetaData);
        }
    }

    public replaceFileMetaAfterUpload(fileMetaData: DrFileMeta): void {
        if (fileMetaData && fileMetaData.document_type) {
            const fileMetaList = this.getOrCreateFileMetaListForCategory(fileMetaData.document_type);
            const fileToReplace = _.find(fileMetaList.entries, (file: DrFileMeta) => {
                // Check if both have an ID and if they are the same (EDIT FILE)
                // If one or both have no ID, then we check for state LOADING and same filename (NEW FILE)
                return (file.id && fileMetaData.id && file.id === fileMetaData.id) ||
                       (file.status === FileUploadStatus.LOADING && file.filename === fileMetaData.filename);
            });

            const idx = fileMetaList.entries.indexOf(fileToReplace);

            if (idx !== -1) {
                fileMetaList.entries[idx] = fileMetaData;
            }
        }
    }

    public getNextOrderForCategory(category: string): number {
        const fileMetaList = this.getOrCreateFileMetaListForCategory(category);
        return fileMetaList && fileMetaList.nextOrder;
    }

    public sortAllFileMetaDatabyOrder(): void {
        _.forEach(this.items, (fileMetaList: DrFileMetaList) => fileMetaList.sortFileMetaDatabyOrder());
    }

    public updateFileMetaOrderAfterSuccessFullPatch(patchData: DrFileMetaOpData[]): void {
        _.forEach(patchData, (drFileMetaPatch: DrFileMetaOpData) => {
            const fileMetaList = this.getFilesByCategory(drFileMetaPatch.document_type);
            const fileMeta = fileMetaList.entries.find(metaData => metaData.id === drFileMetaPatch.id);
            fileMeta.order = drFileMetaPatch.value;
        });
    }
}

export class DrFileUploadMeta extends DrFileMeta {
    public fileAsBinaryString: string;
}
